import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Bot, Sparkles, Loader2, CornerDownLeft, Wand2, ChevronDown, ChevronUp, MessageSquare, Settings2, Smile, Palette, Eye } from 'lucide-react';
import { useAiChat } from '@/contexts/GenUIContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { withOrganizationId } from '@/lib/api/organization';
import { getAiAnalyticsUrl } from '@/lib/api/aiAnalyticsUrl';
import { useNavigate } from 'react-router-dom';

interface Message {
    id: string;
    role: 'ai' | 'user';
    content: string;
    timestamp: Date;
    navigationUrl?: string;
    navigationTitle?: string;
    suggestions?: string[];
}

interface NavigationPreview {
    title: string;
    section: string;
    description: string;
    path: string;
    previewUrl: string;
    isInternal: boolean;
}

type NavigationPreviewMeta = Pick<NavigationPreview, 'section' | 'description'>;

// ─── Draggable / corner-snap config ─────────────────────────────────────────
// The widget floats in a bottom corner. Users can drag it by the header; on
// release it snaps to whichever bottom corner (left/right) is nearest. The
// chosen corner persists across sessions.
const EDGE_GAP = 24; // px inset from the viewport edge (matches the old bottom-6/right-6)
const DRAG_THRESHOLD = 4; // px of movement before a press counts as a drag
const CORNER_STORAGE_KEY = 'nexa_widget_corner';
const INTRO_STORAGE_KEY = 'nexa_widget_intro_pending';

type Corner = 'left' | 'right';

const clamp = (value: number, lo: number, hi: number) =>
    Math.max(lo, Math.min(value, Math.max(lo, hi)));

const readStoredCorner = (): Corner => {
    try {
        return localStorage.getItem(CORNER_STORAGE_KEY) === 'left' ? 'left' : 'right';
    } catch {
        return 'right';
    }
};

const MOCK_MESSAGES: Message[] = [
    {
        id: '1',
        role: 'ai',
        content: "Hi there! I’m Nexa 👋 your AI assistant. What can I help you with today?",
        timestamp: new Date(),
    },
];

const formatAiResponse = (message: string, steps?: string[]) => {
    const safeMessage = (message || '').trim();
    const normalizedSteps = Array.isArray(steps)
        ? steps.map(s => String(s || '').trim()).filter(Boolean)
        : [];

    if (normalizedSteps.length > 0) {
        const numbered = normalizedSteps.map((s, i) => `${i + 1}. ${s}`);
        return `${safeMessage}\n\nSteps:\n${numbered.join('\n')}`.trim();
    }

    // If steps not provided but message contains inline "Step X:" patterns, format them
    if (/Step\s*\d+:/i.test(safeMessage)) {
        const parts = safeMessage.split(/Step\s*\d+:\s*/i).map(p => p.trim()).filter(Boolean);
        if (parts.length > 1) {
            const [intro, ...rest] = parts;
            const numbered = rest.map((s, i) => `${i + 1}. ${s}`);
            return `${intro}\n\nSteps:\n${numbered.join('\n')}`.trim();
        }
    }

    return safeMessage;
};

const previewByRootPath: Record<string, NavigationPreviewMeta> = {
    campaign: {
        section: 'Campaigns',
        description: 'Create, schedule, and monitor campaign performance.',
    },
    campaigns: {
        section: 'Campaigns',
        description: 'Create, schedule, and monitor campaign performance.',
    },
    contacts: {
        section: 'CRM',
        description: 'Manage contacts, lists, and profile details.',
    },
    crm: {
        section: 'CRM',
        description: 'Manage contacts, lists, and profile details.',
    },
    automation: {
        section: 'Automations',
        description: 'Build and manage automation flows and forms.',
    },
    flows: {
        section: 'Automations',
        description: 'Build and manage automation flows and forms.',
    },
    inbox: {
        section: 'Inbox',
        description: 'Review and respond to incoming conversations.',
    },
    reports: {
        section: 'Reports',
        description: 'Track usage, performance, and analytics.',
    },
    'data-store': {
        section: 'Data Store',
        description: 'Access structured records and synced data.',
    },
};

export const ChatWidget: React.FC = () => {
    const { isOpen, setIsOpen } = useAiChat();
    const navigate = useNavigate();
    const [messages, setMessages] = useState<Message[]>(MOCK_MESSAGES);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [typingElapsed, setTypingElapsed] = useState(0);
    const [sidePreview, setSidePreview] = useState<NavigationPreview | null>(null);
    const [sidePreviewPos, setSidePreviewPos] = useState<{ top: number; left: number } | null>(null);
    const hidePreviewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const widgetRef = useRef<HTMLDivElement>(null);
    const [isMinimized, setIsMinimized] = useState(false);

    // Corner-snap dragging. `corner` is the resting anchor; `dragPos` holds the
    // live top-left while a drag is in flight (null when settled). A ref tracks
    // the in-flight gesture so pointer handlers stay allocation-free.
    const [corner, setCorner] = useState<Corner>(readStoredCorner);
    const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
    const dragRef = useRef<
        { startX: number; startY: number; originX: number; originY: number; width: number; height: number; moved: boolean } | null
    >(null);
    const quickPrompts = [
        'Take me to Flows page',
        'Show where campaigns are',
        'Where can I manage contacts?',
    ];

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen, isMinimized]);

    useEffect(() => {
        if (!isTyping) {
            setTypingElapsed(0);
            return;
        }

        const interval = window.setInterval(() => {
            setTypingElapsed((prev) => prev + 1);
        }, 1100);

        return () => window.clearInterval(interval);
    }, [isTyping]);

    useEffect(() => {
        return () => {
            if (hidePreviewTimeoutRef.current) {
                clearTimeout(hidePreviewTimeoutRef.current);
            }
        };
    }, []);

    // First-time-signup auto-open. A durable flag is set at signup; we consume it
    // exactly once — either when onboarding fires `nexa:intro-open` in the same
    // session, or on a later mount once onboarding is already recorded complete.
    useEffect(() => {
        const consumeIntro = () => {
            try {
                if (localStorage.getItem(INTRO_STORAGE_KEY) !== 'true') return;
                localStorage.removeItem(INTRO_STORAGE_KEY);
            } catch {
                return;
            }
            setIsMinimized(false);
            setIsOpen(true);
        };

        window.addEventListener('nexa:intro-open', consumeIntro);
        // Reload path: onboarding finished before the widget consumed the flag.
        try {
            if (localStorage.getItem('onboarding_survey_completed') === 'true') {
                consumeIntro();
            }
        } catch {
            // localStorage unavailable — nothing to consume.
        }
        return () => window.removeEventListener('nexa:intro-open', consumeIntro);
    }, [setIsOpen]);

    // Keep the resting widget inside the viewport if the window shrinks below the
    // stored corner's inset (e.g. rotating a phone). Only runs while settled.
    useEffect(() => {
        const onResize = () => {
            if (dragRef.current) return; // mid-drag clamping is handled in the move handler
            setDragPos(null); // re-anchor to the corner via CSS
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const handleDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
        // Never start a drag from the minimize / close buttons.
        if ((e.target as HTMLElement).closest('button')) return;
        const rect = widgetRef.current?.getBoundingClientRect();
        if (!rect) return;
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            originX: rect.left,
            originY: rect.top,
            width: rect.width,
            height: rect.height,
            moved: false,
        };
        setDragPos({ x: rect.left, y: rect.top });
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
            // setPointerCapture can throw if the pointer is already gone — ignore.
        }
    };

    const handleDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag) return;
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        if (!drag.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) drag.moved = true;
        if (!drag.moved) return;
        const x = clamp(drag.originX + dx, EDGE_GAP, window.innerWidth - drag.width - EDGE_GAP);
        const y = clamp(drag.originY + dy, EDGE_GAP, window.innerHeight - drag.height - EDGE_GAP);
        setDragPos({ x, y });
    };

    const handleDragEnd = (e: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        dragRef.current = null;
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
            // ignore
        }
        // A press with no real movement is a click, not a drag — leave the corner.
        if (!drag || !drag.moved) {
            setDragPos(null);
            return;
        }
        const rect = widgetRef.current?.getBoundingClientRect();
        const centerX = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
        const nextCorner: Corner = centerX < window.innerWidth / 2 ? 'left' : 'right';
        setCorner(nextCorner);
        try {
            localStorage.setItem(CORNER_STORAGE_KEY, nextCorner);
        } catch {
            // persistence is best-effort
        }
        setDragPos(null); // snap back down to the chosen bottom corner
    };

    const handleSendMessage = async (externalInput?: string) => {
        const content = (externalInput ?? inputValue).trim();
        if (!content) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content,
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInputValue('');
        setIsTyping(true);

        try {
            const token = localStorage.getItem('auth_token');
            const response = await fetch(getAiAnalyticsUrl('/ask-ai/query'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(withOrganizationId({ query: content })),
            });

            if (!response.ok) {
                throw new Error(`Ask AI failed: HTTP ${response.status}`);
            }

            const data = await response.json();
            const aiContent = formatAiResponse(data?.message, data?.steps);
            const navigationUrl = (data?.page_url || data?.navigation?.url || '').trim();
            const navigationTitle = (data?.navigation?.title || '').trim();
            const suggestions = Array.isArray(data?.suggestions)
                ? data.suggestions.map((item: unknown) => String(item || '').trim()).filter(Boolean)
                : [];

            const aiMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'ai',
                content: aiContent || 'Sorry, I could not find an answer.',
                timestamp: new Date(),
                navigationUrl: navigationUrl || undefined,
                navigationTitle: navigationTitle || undefined,
                suggestions,
            };
            setMessages((prev) => [...prev, aiMessage]);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Sorry, something went wrong.';
            const aiMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'ai',
                content: message,
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, aiMessage]);
        } finally {
            setIsTyping(false);
        }
    };

    if (!isOpen) return null;

    const thinkingLabel =
        typingElapsed < 2
            ? 'Understanding your request'
            : typingElapsed < 4
                ? 'Checking the best navigation route'
                : 'Composing a clean answer';

    const renderMessageContent = (content: string) => {
        const lines = content.split('\n');
        return (
            <div className="space-y-2">
                {lines.map((line, idx) => {
                    const trimmed = line.trim();
                    if (!trimmed) {
                        return <div key={`${idx}-space`} className="h-1" />;
                    }

                    const numbered = trimmed.match(/^(\d+)\.\s+(.*)$/);
                    if (numbered) {
                        return (
                            <div key={`${idx}-num`} className="flex items-start gap-2">
                                <span className="mt-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-500/15 px-1 text-[10px] font-semibold text-emerald-700">
                                    {numbered[1]}
                                </span>
                                <p className="leading-relaxed">{numbered[2]}</p>
                            </div>
                        );
                    }

                    const bulleted = trimmed.match(/^[-*]\s+(.*)$/);
                    if (bulleted) {
                        return (
                            <div key={`${idx}-bullet`} className="flex items-start gap-2">
                                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-emerald-500/70" />
                                <p className="leading-relaxed">{bulleted[1]}</p>
                            </div>
                        );
                    }

                    if (/^steps:?$/i.test(trimmed)) {
                        return (
                            <p key={`${idx}-steps`} className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                Steps
                            </p>
                        );
                    }

                    return (
                        <p key={`${idx}-text`} className="leading-relaxed">
                            {trimmed}
                        </p>
                    );
                })}
            </div>
        );
    };

    const handleNavigationClick = (url: string) => {
        if (!url) return;
        if (url.startsWith('/')) {
            navigate(url);
            return;
        }
        try {
            const parsed = new URL(url);
            if (parsed.origin === window.location.origin) {
                navigate(`${parsed.pathname}${parsed.search}${parsed.hash}`);
                return;
            }
        } catch {
            // ignore and fall back to hard navigation
        }
        window.location.href = url;
    };

    const getNavigationPreview = (url: string, title?: string): NavigationPreview => {
        const defaultPreview: NavigationPreview = {
            title: title?.trim() || 'Suggested Page',
            section: 'Navigation',
            description: 'Open the suggested page in your workspace.',
            path: url,
            previewUrl: url,
            isInternal: url.startsWith('/'),
        };

        if (!url) return defaultPreview;

        let path = url.trim();
        if (!path.startsWith('/')) {
            try {
                path = new URL(path).pathname || path;
            } catch {
                return defaultPreview;
            }
        }

        const normalizedPath = path.startsWith('/home/') ? path.slice(5) : path;
        const root = normalizedPath.replace(/^\//, '').split('/')[0]?.toLowerCase();
        const config = root ? previewByRootPath[root] : undefined;

        return {
            title: title?.trim() || (config?.section ? `${config.section} Page` : 'Suggested Page'),
            section: config?.section || 'Navigation',
            description: config?.description || 'Open the suggested page in your workspace.',
            path,
            previewUrl: path.startsWith('/') ? `${path}${path.includes('?') ? '&' : '?'}chat_preview=1` : path,
            isInternal: path.startsWith('/'),
        };
    };

    const showSidePreview = (preview: NavigationPreview, triggerEl: HTMLElement) => {
        if (hidePreviewTimeoutRef.current) {
            clearTimeout(hidePreviewTimeoutRef.current);
            hidePreviewTimeoutRef.current = null;
        }

        const widgetRect = widgetRef.current?.getBoundingClientRect();
        const triggerRect = triggerEl.getBoundingClientRect();
        const panelWidth = 340;
        const margin = 12;

        // Prefer showing the preview to the left of the widget, but if the widget
        // is snapped to the left corner there's no room — flip it to the right.
        let left: number;
        if (widgetRect) {
            const leftCandidate = widgetRect.left - panelWidth - margin;
            left = leftCandidate >= 12
                ? leftCandidate
                : Math.min(widgetRect.right + margin, window.innerWidth - panelWidth - 12);
        } else {
            left = Math.max(12, triggerRect.left - panelWidth - margin);
        }
        const top = Math.max(12, Math.min(window.innerHeight - 280, triggerRect.top - 56));

        setSidePreview(preview);
        setSidePreviewPos({ top, left });
    };

    const hideSidePreviewDelayed = () => {
        if (hidePreviewTimeoutRef.current) {
            clearTimeout(hidePreviewTimeoutRef.current);
        }
        hidePreviewTimeoutRef.current = setTimeout(() => {
            setSidePreview(null);
            setSidePreviewPos(null);
        }, 120);
    };

    return (
        <AnimatePresence>
            <motion.div
                ref={widgetRef}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className={cn(
                    "fixed z-[100] flex flex-col overflow-hidden rounded-[30px] border-2 border-emerald-200/80 bg-[#f8fbfa] pointer-events-auto",
                    "shadow-[0_34px_85px_-35px_rgba(15,23,42,0.48)]",
                    isMinimized ? "h-14 w-80" : "h-[620px] max-h-[86vh] w-[460px] max-w-[calc(100vw-1.5rem)] min-w-[340px]"
                )}
                style={{
                    touchAction: 'none',
                    ...(dragPos
                        ? { left: `${dragPos.x}px`, top: `${dragPos.y}px` }
                        : corner === 'left'
                            ? { left: `${EDGE_GAP}px`, bottom: `${EDGE_GAP}px` }
                            : { right: `${EDGE_GAP}px`, bottom: `${EDGE_GAP}px` }),
                }}
            >
                {/* Header / Drag Handle */}
                <div
                    onPointerDown={handleDragStart}
                    onPointerMove={handleDragMove}
                    onPointerUp={handleDragEnd}
                    onPointerCancel={handleDragEnd}
                    className={cn(
                        "flex shrink-0 cursor-grab select-none items-center justify-between p-2.5 text-white active:cursor-grabbing",
                        isMinimized && "h-full px-5 py-0"
                    )}
                    style={{ backgroundImage: 'linear-gradient(135deg, #0f1d3f 6%, #182f4a 46%, #1fa768 100%)' }}
                >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                        {!isMinimized && (
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/16">
                                <Bot size={14} />
                            </div>
                        )}
                        <div className="min-w-0 space-y-0.5">
                            <h3 className={cn("truncate font-semibold leading-tight tracking-tight", isMinimized ? "text-lg" : "text-2xl")}>
                                NEXA AI
                            </h3>
                            {!isMinimized && (
                                <p className="truncate text-[10px] font-medium text-white/85">
                                    Fast guidance across your workspace
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="ml-3 flex shrink-0 items-center gap-1">
                        {!isMinimized && (
                            <span className="mr-1 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold tracking-wide text-white/95">
                                LIVE
                            </span>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-white hover:bg-black/20 hover:text-white transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsMinimized(!isMinimized);
                            }}
                        >
                            {isMinimized ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                        </Button>
                        {!isMinimized && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-white hover:bg-red-500/80 hover:text-white transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsOpen(false);
                                }}
                            >
                                <X size={15} />
                            </Button>
                        )}
                    </div>
                </div>

                {!isMinimized && (
                    <>
                        {/* Messages Area */}
                        <div className="flex-1 space-y-6 overflow-y-auto bg-gradient-to-b from-[#f4f8f7] via-[#f8fbfa] to-[#eef6f3] px-6 py-5">
                            {messages.length <= 1 && (
                                <div className="rounded-2xl border border-emerald-200 bg-[#eff7f3] p-4">
                                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-emerald-800">
                                        <Wand2 size={14} />
                                        Quick navigation prompts
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {quickPrompts.map((prompt) => (
                                            <button
                                                key={prompt}
                                                type="button"
                                                className="rounded-full border border-emerald-200 bg-[#f8fffc] px-4 py-2 text-sm font-medium text-emerald-800 transition-colors hover:bg-emerald-100"
                                                disabled={isTyping}
                                                onClick={() => handleSendMessage(prompt)}
                                            >
                                                {prompt}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {messages.map((msg) => (
                                <div
                                    key={msg.id}
                                    className={cn(
                                        "flex max-w-[92%] flex-col",
                                        msg.role === 'user' ? "ml-auto items-end" : "mr-auto items-start"
                                    )}
                                >
                                    <div className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
                                        {msg.role === 'ai' ? <Sparkles size={13} /> : <CornerDownLeft size={13} />}
                                        <span>{msg.role === 'ai' ? 'NEXA AI' : 'You'}</span>
                                    </div>
                                    <div
                                        className={cn(
                                            "rounded-[20px] px-6 py-5 text-base leading-relaxed shadow-sm",
                                            msg.role === 'user'
                                                ? "rounded-tr-none border border-emerald-200 bg-[#f8fffc] text-slate-700"
                                                : "rounded-tl-none border border-slate-200 bg-white text-slate-800"
                                        )}
                                    >
                                        {renderMessageContent(msg.content)}
                                    </div>
                                    {msg.role === 'ai' && (msg.suggestions?.length || 0) > 0 && (
                                        <div className="mt-2 grid w-full grid-cols-2 gap-2">
                                            {msg.suggestions!.map((suggestion, idx) => (
                                                <button
                                                    key={`${msg.id}-${suggestion}`}
                                                    type="button"
                                                    onClick={() => handleSendMessage(suggestion)}
                                                    className={cn(
                                                        "w-full rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-100",
                                                        msg.suggestions!.length % 2 === 1 && idx === msg.suggestions!.length - 1 && "col-span-2"
                                                    )}
                                                    disabled={isTyping}
                                                >
                                                    {suggestion}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <span className="mt-2 text-xs text-slate-500">
                                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    {msg.role === 'ai' && msg.navigationUrl && (
                                        <div className="mt-1">
                                            <button
                                                type="button"
                                                onClick={() => handleNavigationClick(msg.navigationUrl!)}
                                                onMouseEnter={(e) => showSidePreview(getNavigationPreview(msg.navigationUrl!, msg.navigationTitle), e.currentTarget)}
                                                onMouseLeave={hideSidePreviewDelayed}
                                                onFocus={(e) => showSidePreview(getNavigationPreview(msg.navigationUrl!, msg.navigationTitle), e.currentTarget)}
                                                onBlur={hideSidePreviewDelayed}
                                                className="text-xs font-semibold text-emerald-700 underline-offset-2 hover:underline"
                                            >
                                                {`Navigate: ${getNavigationPreview(msg.navigationUrl, msg.navigationTitle).title}`}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {isTyping && (
                                <div className="max-w-[92%] rounded-[20px] rounded-tl-none border border-slate-200 bg-white px-6 py-5 text-foreground shadow-sm">
                                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                                        <Loader2 size={13} className="animate-spin" />
                                        {thinkingLabel}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-emerald-500/70 [animation-delay:-0.3s]" />
                                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-emerald-500/70 [animation-delay:-0.15s]" />
                                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-emerald-500/70" />
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <div className="shrink-0 border-t border-emerald-200/70 bg-[#f8fbfa] p-4">
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSendMessage();
                                }}
                                className="space-y-0"
                            >
                                <div className="flex items-end gap-2 rounded-[22px] border-2 border-emerald-200 bg-[#edf5f3] px-3 py-2 shadow-inner">
                                    <textarea
                                        placeholder="Ask for navigation help, actions, or a quick explanation..."
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendMessage();
                                            }
                                        }}
                                        rows={1}
                                        className="min-h-[34px] flex-1 resize-none bg-transparent px-1 pt-1 text-sm text-slate-700 outline-none placeholder:text-slate-400"
                                    />
                                    <Button
                                        type="submit"
                                        size="icon"
                                        disabled={!inputValue.trim() || isTyping}
                                        className="h-10 w-10 shrink-0 rounded-lg text-white shadow-md transition-transform hover:scale-105"
                                        style={{ backgroundColor: '#9adab8' }}
                                    >
                                        <Send size={16} />
                                    </Button>
                                </div>
                            </form>

                        </div>
                    </>
                )}
            </motion.div>

            {sidePreview && sidePreviewPos && !isMinimized && (
                <div
                    className="fixed z-[110] w-[340px] rounded-xl border border-emerald-200 bg-white p-3 shadow-xl"
                    style={{ top: `${sidePreviewPos.top}px`, left: `${sidePreviewPos.left}px` }}
                    onMouseEnter={() => {
                        if (hidePreviewTimeoutRef.current) {
                            clearTimeout(hidePreviewTimeoutRef.current);
                            hidePreviewTimeoutRef.current = null;
                        }
                    }}
                    onMouseLeave={hideSidePreviewDelayed}
                >
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Page Preview</p>
                    <p className="mt-1 text-sm font-semibold text-slate-800">{sidePreview.title}</p>
                    {sidePreview.isInternal ? (
                        <div className="mt-2 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                            <div className="h-[190px] overflow-hidden">
                                <div
                                    className="origin-top-left scale-[0.34]"
                                    style={{ width: '294%', height: '294%' }}
                                >
                                    <iframe
                                        title={`Preview ${sidePreview.title}`}
                                        src={sidePreview.previewUrl}
                                        loading="lazy"
                                        className="h-[560px] w-[1000px] border-0 bg-white"
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p className="mt-1 text-xs text-slate-500">{sidePreview.description}</p>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                            {sidePreview.section}
                        </span>
                        <span className="max-w-[190px] truncate text-[10px] text-slate-400">{sidePreview.path}</span>
                    </div>
                </div>
            )}
        </AnimatePresence>
    );
};
